import { Backer } from '@givebacks/api/backer';
import { DateTime } from 'luxon';
import { Group, Stack, Text, ThemeIcon } from 'ui';
import { IconDesktop, IconMobile } from 'ui/Icons';

import { createColumnHelper } from '../../../hooks/tables';
import { columnsCreator } from '../../../utils/csv/csv';
import { centsToDollar } from '../../../utils/currency';

const columnHelper = createColumnHelper<Backer>();

export const BackerInfoColumns = [
  columnHelper.accessor('name', {
    header: 'Backer Name',
    cell: (props) => (
      <Stack gap={0}>
        <Text type="caption" c="gray">
          {props.row.original.name.trim() == ',' ? ' ' : props.row.original.name}
        </Text>
        <Text fz="xxxs" c="gray">
          {props.row.original.email}
        </Text>
      </Stack>
    ),
    size: 300,
    enablePinning: true,
  }),
  columnHelper.accessor('created_at', {
    header: 'Sign up Date',
    cell: (props) => {
      const date = DateTime.fromISO(props.row.original.created_at).toLocaleString();
      return date;
    },
    size: 102,
  }),
  columnHelper.accessor('last_activity_at', {
    header: 'Last Activity Date',
    cell: (props) => {
      if (!props.row.original.last_activity_at) return null;
      const date = DateTime.fromISO(props.row.original.last_activity_at).toLocaleString();
      return date;
    },
    size: 102,
  }),
  columnHelper.accessor('purchases', {
    header: 'Purchases',
    cell: (props) => props.getValue(),
    size: 92,
  }),
  columnHelper.accessor('amount_raised', {
    header: 'Amount Raised',
    cell: (props) => centsToDollar(props.getValue()),
    size: 121,
  }),
  columnHelper.accessor('mobile_extension_enabled', {
    header: 'Extensions',
    id: 'extensions',
    cell: (props) => (
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon
          bg="transparent"
          c={props.row.original.mobile_extension_enabled === true ? 'gray.8' : 'gray.2'}
          size="sm"
        >
          <IconMobile />
        </ThemeIcon>
        <ThemeIcon
          bg="transparent"
          c={props.row.original.chrome_extension_enabled === true ? 'gray.8' : 'gray.2'}
          size="sm"
        >
          <IconDesktop />
        </ThemeIcon>
      </Group>
    ),
    enableSorting: false,
    size: 92,
  }),
  columnHelper.accessor('number_of_cards', {
    header: 'Cards On File',
    size: 108,
  }),
  columnHelper.accessor('referrals', {
    header: 'Referral Sign Ups',
    size: 132,
  }),
  columnHelper.accessor('address', {
    header: 'Address',
    enableSorting: false,
    size: 150,
  }),
  columnHelper.accessor('state', {
    header: 'State',
    size: 60,
  }),
  columnHelper.accessor('zip_code', {
    header: 'Zip Code',
    size: 100,
  }),
];

export const exportColumnsCreator = columnsCreator<Backer>();

export const exportBackerInfoColumns = [
  exportColumnsCreator.create({
    header: 'Name',
    cell: (row) => (row.name.trim() == ',' ? undefined : row.name),
  }),

  exportColumnsCreator.create({
    header: 'Email',
    cell: (row) => row.email,
  }),
  exportColumnsCreator.create({
    header: 'Sign up Date',
    cell: (row) => {
      const date = DateTime.fromISO(row.created_at).toLocaleString();
      return date;
    },
  }),
  exportColumnsCreator.create({
    header: 'Last Activity Date',
    cell: (row) => {
      if (!row.last_activity_at) return null;
      const date = DateTime.fromISO(row.last_activity_at).toLocaleString();
      return date;
    },
  }),
  exportColumnsCreator.create({
    header: 'Purchases',
    cell: (row) => row.purchases,
  }),
  exportColumnsCreator.create({
    header: 'Amount Raised',
    cell: (row) => centsToDollar(row.amount_raised),
  }),
  exportColumnsCreator.create({
    header: 'Mobile Extension',
    cell: (row) => row.mobile_extension_enabled,
  }),
  exportColumnsCreator.create({
    header: 'Desktop Extension',
    cell: (row) => row.chrome_extension_enabled,
  }),
  exportColumnsCreator.create({
    header: 'Cards On File',
    cell: (row) => row.number_of_cards,
  }),
  exportColumnsCreator.create({
    header: 'Referral Sign Ups',
    cell: (row) => row.referrals,
  }),
  exportColumnsCreator.create({
    header: 'Address',
    cell: (row) => row.address,
  }),
  exportColumnsCreator.create({
    header: 'State',
    cell: (row) => row.state,
  }),
  exportColumnsCreator.create({
    header: 'Zip Code',
    cell: (row) => row.zip_code,
  }),
];
